/**
 * Cursor - Cuberto Mouse Follower
 *
 * Persistent singleton. One global custom cursor on desktop only.
 * Content-specific behaviour uses the library's data attributes (event delegation).
 *
 * Usage in HTML:
 *   data-cursor="-inverse"       → light cursor on hover (note: -inverse not -invert)
 *   data-cursor="-hidden"        → hide cursor on hover
 *   data-cursor-text="View"      → show text in cursor
 *   data-cursor-img="/path"      → show image
 *   data-cursor-stick="#el"      → stick to element
 *
 * For a whole section (e.g. dark hero): wrap in an element with data-cursor="-inverse".
 */

import MouseFollower from "mouse-follower";
import gsap from "gsap";
import { isMobile } from "../utils/media";
import { reduced } from "../gsap";

MouseFollower.registerGSAP(gsap);

class _Cursor {
  #instance = null;
  #initialized = false;

  get instance() {
    return this.#instance;
  }

  init() {
    if (this.#initialized) return;
    if (isMobile() || reduced) return;

    this.#initialized = true;
    document.body.classList.add("has-custom-cursor");

    this.#instance = new MouseFollower({
      container: document.body,
      className: "mf-cursor",
      innerClassName: "mf-cursor-inner",
      textClassName: "mf-cursor-text",
      mediaClassName: "mf-cursor-media",
      mediaBoxClassName: "mf-cursor-media-box",
      dataAttr: "cursor",
      visible: true,
      speed: 0.2,
      ease: "expo.out",
      hideOnLeave: true,
      hideTimeout: 300,
      stateDetection: {
        "-pointer": "a, button, [role='button']",
        "-hidden": "iframe, input, textarea, select",
      },
    });
  }

  show() {
    if (this.#instance) this.#instance.show();
  }

  hide() {
    if (this.#instance) this.#instance.hide();
  }

  destroy() {
    if (this.#instance) {
      this.#instance.destroy();
      this.#instance = null;
    }
    document.body.classList.remove("has-custom-cursor");
    this.#initialized = false;
  }
}

export const Cursor = new _Cursor();
export { _Cursor };
